import styled from 'styled-components';
import React, {FC} from 'react';
import {
  fontWeights,
  mediaBreakpointDown,
  transition,
  mediaBreakpointUp
} from '../../../../lib/styleVariables';
import {ImageTextCarouselItem, ImageTextCarouselItemProps} from './item';
import {Image} from '../../../common/image';
import {StylableProps} from '../../../../lib/styleHelpers';
import {fontSizesMixinsMobileR4, fontSizesMixinsR4, paletteR4} from '../../../../lib/styleVariablesR4';
import {Icon, IconProps} from '../../../molecules/icon';
import {SecondaryButton} from '../../../molecules/buttonsR4';

interface ImageTextCarouselProps extends StylableProps {
  items: Pick<ImageTextCarouselItemProps, 'content' | 'imgSrc' | 'title' | 'buttonText' | 'buttonLink'>[];
  iconName?: IconProps['name'];
  eyebrow: string;
  title: string;
  imagePosition: 'left' | 'right';
  titleColor?: string;
  description?: string | null;
  linkText?: string | null;
  linkSlug?: string | null;
}

type ImagePositionProps = Pick<ImageTextCarouselProps, 'imagePosition'>;

const StyledIcon = styled(Icon)`
  margin: 0 auto 24px auto;
  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 8px;
  }
`;

const StyledTextWrapper = styled.div`
  margin-bottom: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 40px;
  }
`;

const StyledEyebrow = styled.h6`
  ${fontSizesMixinsR4.h6()};
  margin: 0 0 40px 0;
  color: ${paletteR4.black.base};
  text-transform: uppercase;
  text-align: center;

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h6()};
    margin: 0 0 24px 0;
  }
`;

const StyledTitle = styled.h2<{titleColor?: string}>`
  ${fontSizesMixinsR4.h2()};
  margin: 0;
  color: ${props => props.titleColor};
  max-width: 840px;
  width: 100%;
  text-align: center;

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h3()};
    max-width: 420px;
  }
`;
const StyledImg = styled(Image)<{isSelect: boolean}>`
  grid-column: 1 / auto;
  grid-row: 1 / auto;
  display: block;
  width: 100%;
  border-radius: 10px;
  transition: opacity ${transition.fast} ease-in-out;
  opacity: ${props => (props.isSelect ? 1 : 0)};
  max-height: 534px;
  ${mediaBreakpointDown('tablet')} {
    display: none;
  }
`;

const StyledImageWrapper = styled.div`
  position: relative;
  width: 720px;
  display: grid;
  ${mediaBreakpointDown('tablet')} {
    display: none;
  }
`;

const StyledBodyContainer = styled.div<ImagePositionProps>`
  display: flex;
  flex-flow: ${props => (props.imagePosition === 'left' ? 'row' : 'row-reverse')} nowrap;
  justify-content: space-between;
  align-items: flex-start;
  ${mediaBreakpointDown('tablet')} {
    justify-content: center;
  }
`;

const getContentMargin = (props: ImagePositionProps & {hasNoImage: boolean}) => {
  if (props.hasNoImage)
    return '0';
  if (props.imagePosition === 'left')
    return '0 0 0 60px';

  return '0 60px 0 0';
};

const StyledContentWrapper = styled.div<ImagePositionProps & {hasNoImage: boolean}>`
  width: 100%;
  max-width: ${props => (props.hasNoImage ? '100%' : '420px')};
  display: flex;
  flex-direction: column;
  ${mediaBreakpointUp('desktop')} {
    margin: ${props => getContentMargin(props)};
  }
  ${mediaBreakpointDown('tablet')} {
    padding: 0;
    align-items: center;
  }
`;

const StyledDesktopCarouselItemWrapper = styled.div<{titleColor?: string; isOpen: boolean}>`
  border-top: 1px solid
    ${props => (props.isOpen && props.titleColor ? props.titleColor : paletteR4.black.base)};
  width: 100%;

  &:last-child {
    border-bottom: 1px solid ${paletteR4.black.base};
  }
`;

const StyledDescription = styled.div`
  ${fontSizesMixinsR4.p1()};
  font-weight: ${fontWeights.semibold};
  text-align: center;
  margin-top: 60px;
  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsR4.p2()};
    font-weight: ${fontWeights.bold};
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)<{titleColor?: string}>`
  margin: 60px auto 0;
  background-color: transparent;
  max-width: max-content;
  color: ${props => props.titleColor || 'inherit'};
  border-color: ${props => props.titleColor || 'inherit'};
  ${mediaBreakpointDown('tablet')} {
    margin: 48px auto 0;
  }
  &:hover {
    background-color: ${props => props.titleColor || 'inherit'};
  }
`;

export const ImageTextCarousel: FC<ImageTextCarouselProps> = props => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const hasImage = (item: any) => item.imgSrc?.src;

  const hasNoImage = props.items.every((item: any) => !hasImage(item));

  return (
    <div className={props.className}>
      {props.iconName && <StyledIcon useR4 name={props.iconName} size={80} />}
      <StyledTextWrapper>
        {props.eyebrow && <StyledEyebrow>{props.eyebrow}</StyledEyebrow>}
        {props.title && <StyledTitle titleColor={props.titleColor}>{props.title}</StyledTitle>}
      </StyledTextWrapper>
      <StyledBodyContainer imagePosition={props.imagePosition}>
        {!hasNoImage && (
          <StyledImageWrapper>
            {props.items.map((item, index) => (
              <StyledImg fluid={item.imgSrc} isSelect={index === selectedIndex} />
            ))}
          </StyledImageWrapper>
        )}
        <StyledContentWrapper hasNoImage={hasNoImage} imagePosition={props.imagePosition}>
          {props.items.map((item, index) => (
            <StyledDesktopCarouselItemWrapper
              key={item.title}
              isOpen={index === selectedIndex}
              titleColor={props.titleColor}
              onClick={() => setSelectedIndex(index)}
            >
              <ImageTextCarouselItem
                {...item}
                hasImage={hasImage(item)}
                selectedItemColor={props.titleColor}
                isOpen={index === selectedIndex}
              />
            </StyledDesktopCarouselItemWrapper>
          ))}
        </StyledContentWrapper>
      </StyledBodyContainer>
      {Boolean(props.description || props.linkText) && (
        <>
          {props.description && (
            <StyledDescription className="description">{props.description}</StyledDescription>
          )}
          <StyledSecondaryButton titleColor={props.titleColor}>{props.linkText}</StyledSecondaryButton>
        </>
      )}
    </div>
  );
};
